import 'ag-grid-community/dist/styles/ag-grid.css';
import 'ag-grid-community/dist/styles/ag-theme-material.css';
import './custom-ag-grid.scss';

import React, { Component } from 'react';
import { AgGridReact } from 'ag-grid-react';
import PropTypes from 'prop-types';
import { get, merge, omit, isEqual }  from 'lodash';

import { TABLE_TYPES } from './constants/table-options';
import ArrowDownward from  '../../assets/material-arrow-downward.svg';
import ArrowUpward from  '../../assets/material-arrow-upward.svg';
import CustomFilterIcon from '../../assets/custom-filter-icon.svg';
import LoadingSpinner from '../common/components/loading-spinner';
import BasicMultiSelectFilter from './filters/basic-multiselect-filter';
import SingleDatePickerFilterContainer from './filters/single-date-picker-filter-container';
import GreaterOrLessThanFilterContainer from './filters/greater-or-less-than-filter-container';

import columnTypes from './constants/column-types';
import { formatFilename } from './table-utils';

class AgGridTable extends Component{
    componentDidUpdate( prevProps ){
        const { columnDefs, isExportingGridToCSV, isLoading, scrollToTop } = this.props;

        if( isExportingGridToCSV === true && prevProps.isExportingGridToCSV === false ){
            this.exportGridToCSV( this.props.exportCSVFileName );
        }

        if( !isEqual( columnDefs, prevProps.columnDefs ) ){

            /**
             * Even though, columnDefs are refreshed and updated
             * not all of the grid properties are updated.
             * `setColumnDefs` helps AgGrid keep all the functionalities in sync
             * such as sorting, filtering, et.
             */
            this.gridApi && this.gridApi.setColumnDefs( columnDefs );
            this.resizeColumnsAgGrid();
        }

        if( scrollToTop ){
            this.gridApi && this.gridApi.ensureIndexVisible( 0, 'top' );
        }

        if( isLoading ){
            this.gridApi && this.gridApi.showLoadingOverlay();
        }
        else{
            this.gridApi && this.gridApi.hideOverlay();
        }
    }

    buildDefaultGridOptions = () => {
        const { tableType } = this.props;
        const domLayout = tableType.includes( TABLE_TYPES.BASIC ) ? 'autoHeight' : 'normal';

        return {
            columnTypes,
            defaultColDef: {
                filter: true,
                icons: {
                    menu: `<img src="${CustomFilterIcon}" height="16" />`,
                    sortAscending: `<img src="${ArrowUpward}" height="16" />`,
                    sortDescending: `<img src="${ArrowDownward}" height="16" />`,
                },
                menuTabs: [ 'filterMenuTab' ],
                minWidth: 128,
                resizable: true,
                sortable: true,
                width: 128,
            },
            domLayout,
            ensureDomOrder: true,
            frameworkComponents: {
                basicMultiSelectFilter: BasicMultiSelectFilter,
                customLoadingOverlay: LoadingSpinner,
                greaterOrLessThanFilter: GreaterOrLessThanFilterContainer,
                singleDatePickerFilter: SingleDatePickerFilterContainer,
            },
            groupRemoveSingleChildren: true,
            groupSuppressAutoColumn: true,
            loadingOverlayComponent: 'customLoadingOverlay',
            localeText: {
                noRowsToShow: `Hmm, it looks like you don't have data for this date range.`
            },
            rowSelection: 'single',
            suppressAggFuncInHeader: true,
            suppressCellSelection: true,
            suppressContextMenu: true,
            suppressDragLeaveHidesColumns: true,
            suppressMovableColumns: true,
            suppressRowClickSelection: true,
        };
    }

    filterAndCleanColumnDefs = () => {
        const { columnDefs } = this.props;
        let cleanedColumnDefs = columnDefs;
        // explicitly remove custom fields (['active', 'default']) so ag-grid doesn't throw warnings
        return cleanedColumnDefs.map( ( col ) => omit( col, ['active', 'default', 'fieldParams'] ) );
    }

    exportGridToCSV = ( filename = 'csv' ) => {
        const { widgetId, exportToCSVSuccess, exportToCSVFailure } = this.props;
        if( get( this, 'gridApi.exportDataAsCsv', false ) ){
            const params = {
                allColumns: true,
                fileName: formatFilename( filename, 'csv', true ),
                processCellCallback: this.processCellCallback,
                skipGroups: true,
            };

            try{
                this.gridApi.exportDataAsCsv( params );
                exportToCSVSuccess( widgetId );
            }
            catch( error ){
                console.warn( `Error on Aggrid CSV Export: ${error.message}` );
                exportToCSVFailure( widgetId, error );
            }
        }
        else{
            const errorMessage = `AgGrid's gridApi is not initialized`;
            console.warn( errorMessage );
            exportToCSVFailure( widgetId, new Error( errorMessage ) );
        }
    }

    handleRowClick = ( { node } ) => {

        // expand/collapse rowGroups wherever row is clicked instead of limiting trigger to chevron
        if( node.group ){
            node.expanded = !node.expanded;
            this.gridApi.onGroupExpandedOrCollapsed();
        }
        else{
            this.props.rowClickHandler( node.data );
        }
    }

    handleResize = ( params ) => {
        this.gridApi = params.api;
        this.columnApi = params.columnApi;
        if( get( this, 'columnApi.columnController.scrollWidth' ) > 0 ){
            this.resizeColumnsAgGrid();
        }
    };

    processCellCallback = ( params ) => {
        if( typeof params.column.colDef.valueFormatter === 'function' ){
            return params.column.colDef.valueFormatter( params );
        }
        return params.value;
    };

    resizeColumnsAgGrid = () => {
        if( get( this, 'gridApi.sizeColumnsToFit' ) ){
            this.gridApi.sizeColumnsToFit();
        }
    }

    handleFilterChange = ( params ) => {
        this.props.onFilterChanged();
        if( this.props.customGridOptions.onFilterChanged ){
            this.props.customGridOptions.onFilterChanged( params );
        }
    }

    handleSortChange = ( params ) => {
        this.props.onSortChanged();
        if( this.props.customGridOptions.onSortChanged ){
            this.props.customGridOptions.onSortChanged( params );
        }
    }

    render(){
        const { context, customGridOptions, dataAutomationId, rowData, tableType } = this.props;
        const defaultGridOptions = this.buildDefaultGridOptions();

        // order matters so customGridOptions will override any collisions with defaultGridOptions
        // lodash merge will deeply merge nested properties (whereas ES6 spread will override if prop already exists)
        const gridOptions = merge( defaultGridOptions, customGridOptions );

        return (
            <div
                className={`ag-theme-material ${tableType}`}
                data-automation-id={dataAutomationId}
            >
                {/*
                    getBusinessKeyForNode is the way to add the automation ids to the table
                    row-business-key={Value} is what it appears as in the dom.
                    context is a way to pass down additional props to filters. We have to add them directly to the table in order for props to get updated
                    if they are placed in the gridOptions they do not update after initialization.
                */}
                <AgGridReact
                    columnDefs={this.filterAndCleanColumnDefs()}
                    getBusinessKeyForNode={ () => `${dataAutomationId}-grid-row`}
                    gridOptions={gridOptions}
                    onGridSizeChanged={this.handleResize}
                    onGridReady={this.handleResize}
                    onFilterChanged={this.handleFilterChange}
                    onSortChanged={this.handleSortChange}
                    onRowClicked={this.handleRowClick}
                    rowData={rowData}
                    context={context}
                />
            </div>
        );
    }
};

AgGridTable.defaultProps = {
    columnDefs: [],
    customGridOptions: {},
    isLoading: false,
    rowClickHandler: () => {},
    scrollToTop: false,
    tableType: TABLE_TYPES.BASIC,
};

AgGridTable.propTypes = {
    columnDefs: PropTypes.array.isRequired,
    context: PropTypes.object,
    customGridOptions: PropTypes.object.isRequired,
    dataAutomationId: PropTypes.string.isRequired,
    exportCSVFileName: PropTypes.string,
    exportToCSVFailure: PropTypes.func,
    exportToCSVSuccess: PropTypes.func,
    isExportingGridToCSV: PropTypes.bool,
    isLoading: PropTypes.bool.isRequired,
    onFilterChanged: PropTypes.func.isRequired,
    onSortChanged: PropTypes.func.isRequired,
    rowClickHandler: PropTypes.func,
    rowData: PropTypes.array.isRequired,
    scrollToTop: PropTypes.bool,
    tableType: PropTypes.string,
    widgetId: PropTypes.oneOfType( [
        PropTypes.string,
        PropTypes.number,
    ] ),
};

export default AgGridTable;
